import { useQuery } from '@tanstack/react-query';
import { ID } from 'types';
import { fetchChangeLogHistory } from '@api/LineItemHistoriesApi';
import { CHANGE_LOG_KIND } from '@components/bid_request/details/ChangeLogDrawer/types';
import { SuggestedPriceOverrideAttribution } from '@features/suggested-price-column/types/override-attribution';

/** The audited column whose latest change identifies who set the override. */
export const SUGGESTED_PRICE_OVERRIDE_CHANGED_KEY = 'suggested_price_override';

/**
 * Only the newest row is wanted, so the server does the work: filtered to this line item and this
 * column, sorted newest-first, and capped at one record. That keeps the response a single row
 * whatever the row's edit history looks like.
 *
 * The sort is deterministic server-side — `LineItemHistories::Query` breaks `created_at` ties on
 * `id`, which matters because a batch write stamps one shared timestamp across every row it emits.
 * Without that tiebreak "the most recent row" would be an arbitrary member of the tie.
 */
const LATEST_ONLY_PAGE = 1;
const LATEST_ONLY_PER_PAGE = 1;

interface GetOverrideAttributionParams {
  bidRequestId: ID;
  lineItemId: ID;
}

/**
 * Reads the latest `suggested_price_override` change for one line item and reduces it to the pair
 * the drawer's attribution sentence needs.
 *
 * Returns an empty object rather than throwing when there is nobody to attribute. Three ways that
 * happens, all normal:
 *
 * - No audit row at all. The column is writable while its auditing is flag-gated, the backfill
 *   task writes it via raw SQL, and imports replace line items rather than updating them.
 * - The row is not an update. Only update rows carry a `changes` diff; a destroy row leaves it
 *   empty, so there is no before/after pair to read.
 * - The newest change *cleared* the override. Its after value is null, which makes it a reset
 *   rather than a set — whoever did it removed an override instead of setting one, so there is no
 *   current override for them to own. Anything earlier in the history has since been undone.
 *
 * Zero is deliberately NOT absence: clearing a Suggested Price cell persists a real $0 override
 * that suppliers see, so it has an author like any other value.
 */
export const getOverrideAttribution = async ({
  bidRequestId,
  lineItemId,
}: GetOverrideAttributionParams): Promise<SuggestedPriceOverrideAttribution> => {
  const response = await fetchChangeLogHistory({
    bidRequestId,
    page: LATEST_ONLY_PAGE,
    perPage: LATEST_ONLY_PER_PAGE,
    sort: 'created_at',
    sortOrder: 'descend',
    where: {
      changed_keys: [SUGGESTED_PRICE_OVERRIDE_CHANGED_KEY],
      line_item_ids: [lineItemId],
    },
  });

  const latest = response?.records?.[0];

  if (!latest || latest.kind !== CHANGE_LOG_KIND.LINE_ITEM_UPDATE) return {};

  const [, after] = latest.changes?.[SUGGESTED_PRICE_OVERRIDE_CHANGED_KEY] ?? [];

  if (after === null || after === undefined || after === '') return {};

  return { createdAt: latest.created_at, whodunnit: latest.whodunnit };
};

/**
 * Keyed on the override value as well as the line item. The audit trail is append-only, so a long
 * staleTime is right — but saving a new override writes a new newest row, and without the value in
 * the key a reopened drawer would serve the previous editor from cache.
 */
export const overrideAttributionQueryKey = (
  bidRequestId: ID,
  lineItemId: ID,
  overrideValue: number | null,
) => [
  'bid_requests',
  bidRequestId,
  'line_item_histories',
  'override_attribution',
  lineItemId,
  overrideValue,
];

// The audit trail is append-only, so a row's latest override change cannot change while the buyer
// has the drawer open. Long staleTime keeps a reopen from refetching.
const OVERRIDE_ATTRIBUTION_STALE_TIME_MS = 5 * 60 * 1000;

// One retry rather than TanStack's default three: the attribution line is supplementary, so the
// drawer should settle on omitting it quickly rather than holding a pending state.
const OVERRIDE_ATTRIBUTION_RETRY_COUNT = 1;

interface UseGetOverrideAttributionParams {
  bidRequestId: ID;
  /** Gate — only fetch for an overridden row whose drawer is open. Never prefetch. */
  enabled: boolean;
  lineItemId: ID;
  /** Part of the cache key: a new override value means a new newest audit row to read. */
  overrideValue: number | null;
}

/**
 * Both ids are required for the request to address anything. `bidRequestId` reaches the cell as an
 * optional prop, so an absent one would otherwise build `/bid_requests//line_item_histories` and
 * fail on a malformed route rather than simply not asking.
 */
export const useGetOverrideAttribution = ({
  bidRequestId,
  enabled,
  lineItemId,
  overrideValue,
}: UseGetOverrideAttributionParams) => useQuery({
  enabled: enabled && Boolean(bidRequestId) && Boolean(lineItemId),
  queryFn: () => getOverrideAttribution({ bidRequestId, lineItemId }),
  queryKey: overrideAttributionQueryKey(bidRequestId, lineItemId, overrideValue),
  retry: OVERRIDE_ATTRIBUTION_RETRY_COUNT,
  staleTime: OVERRIDE_ATTRIBUTION_STALE_TIME_MS,
});
